import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Heading,
  Text,
  useToast,
  Divider,
  HStack,
  IconButton,
  Checkbox,
  InputGroup,
  InputLeftElement,
  Spinner,
  Image,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, SearchIcon } from "@chakra-ui/icons";
import { useQuery, useMutation } from "react-query";
import { getProducts } from "../services/productService.js";
import { getSettings, updateSettings } from "../services/settingService.js";
import Card from "../components/Card/Card.js";
import CardBody from "../components/Card/CardBody.js";
import CardHeader from "../components/Card/CardHeader.js";

export default function LandingCMS() {
  const toast = useToast();
  const [formData, setFormData] = useState({
    heroTitle: "Discover Your Next Favorite Item",
    heroDesc: "Welcome to SiteStore. We curate the best products from around the world so you don't have to.",
    heroImage: "",
    curatedLists: [{ title: "", productIds: [] }], 
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const formInitialized = React.useRef(false);

  const { isLoading: settingsLoading } = useQuery('siteSettings', getSettings, {
    onSuccess: (data) => {
      if (data && !formInitialized.current) {
        formInitialized.current = true;
        setFormData({
          heroTitle: data.heroTitle || "",
          heroDesc: data.heroDesc || "",
          heroImage: data.heroImage || "",
          curatedLists: data.curatedLists?.length ? data.curatedLists : [{ title: "", productIds: [] }],
        });
      }
    }
  });

  const mutation = useMutation(updateSettings, {
    onSuccess: () => {
      toast({
        title: "بروزرسانی موفق",
        description: "تنظیمات لندینگ با موفقیت ذخیره شد.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: "خطا در ذخیره",
        description: error?.response?.data?.message || "خطایی رخ داد.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  });

  // Debounce search query to prevent spamming the API
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: productsData, isLoading } = useQuery(
    ['allProducts', debouncedSearchQuery],
    () => getProducts({ limit: 100, search: debouncedSearchQuery }), 
    { keepPreviousData: true }
  );

  const products = productsData?.data || productsData?.products || productsData || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, [field]: reader.result || "" }));
    };
    reader.readAsDataURL(file);
  };

  const handleListTitleChange = (index, value) => {
    const newLists = [...formData.curatedLists];
    newLists[index].title = value;
    setFormData({ ...formData, curatedLists: newLists });
  };

  const toggleProductInList = (listIndex, productId) => {
    const newLists = [...formData.curatedLists];
    const list = newLists[listIndex];
    if (list.productIds.includes(productId)) {
      list.productIds = list.productIds.filter((id) => id !== productId);
    } else {
      list.productIds.push(productId);
    }
    setFormData({ ...formData, curatedLists: newLists });
  };

  const addList = () => {
    setFormData({
      ...formData,
      curatedLists: [...formData.curatedLists, { title: "", productIds: [] }],
    });
  };

  const removeList = (index) => {
    const newLists = [...formData.curatedLists];
    newLists.splice(index, 1);
    setFormData({ ...formData, curatedLists: newLists });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      heroTitle: formData.heroTitle,
      heroDesc: formData.heroDesc,
      heroImage: formData.heroImage,
      curatedLists: formData.curatedLists.filter(list => list.title),
    });
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader p="6px 0px 22px 0px">
          <Flex direction="column">
            <Heading as="h3" size="md" mb="4px">
              مدیریت لندینگ پیج
            </Heading>
            <Text color="gray.400" fontSize="sm">
              مدیریت بنر اصلی سایت و لیست محصولات پیشنهادی
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box as="form" onSubmit={handleSubmit} w="100%">
            <VStack spacing={6} align="stretch">
              
              <Heading as="h4" size="sm">تنظیمات بنر (Hero)</Heading>
              
              <FormControl id="heroTitle">
                <FormLabel>عنوان بنر</FormLabel>
                <Input
                  name="heroTitle"
                  value={formData.heroTitle}
                  onChange={handleChange}
                  placeholder="عنوان اصلی سایت"
                />
              </FormControl>

              <FormControl id="heroDesc">
                <FormLabel>توضیحات بنر</FormLabel>
                <Textarea
                  name="heroDesc"
                  value={formData.heroDesc}
                  onChange={handleChange}
                  placeholder="توضیحات کوتاه زیر عنوان"
                />
              </FormControl>

              <FormControl id="heroImage">
                <FormLabel>تصویر بنر</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'heroImage')}
                  mb={2}
                />
                {formData.heroImage && (
                  <Image src={formData.heroImage} alt="Banner Preview" maxH="150px" objectFit="contain" />
                )}
              </FormControl>

              <Divider />

              <Flex justify="space-between" align="center">
                <Heading as="h4" size="sm">لیست‌های محصولات</Heading>
                <Button size="sm" leftIcon={<AddIcon />} colorScheme="teal" onClick={addList}>
                  افزودن لیست جدید
                </Button>
              </Flex>

              {formData.curatedLists.map((list, index) => (
                <Box key={index} p={4} borderWidth="1px" borderRadius="md" position="relative">
                  <IconButton
                    aria-label="حذف لیست"
                    icon={<DeleteIcon />}
                    colorScheme="red"
                    size="sm"
                    position="absolute"
                    top={4}
                    left={4}
                    onClick={() => removeList(index)}
                  />
                  <VStack align="stretch" spacing={4} pr={10}>
                    <FormControl>
                      <FormLabel>عنوان لیست</FormLabel>
                      <Input
                        placeholder="مثال: پرفروش‌ترین‌ها"
                        value={list.title}
                        onChange={(e) => handleListTitleChange(index, e.target.value)}
                      />
                    </FormControl>
                    
                    <Box>
                      <FormLabel>انتخاب محصولات</FormLabel>
                      <InputGroup size="sm" mb={3}>
                        <InputLeftElement pointerEvents="none" children={<SearchIcon color="gray.300" />} />
                        <Input
                          placeholder="جستجوی محصول..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </InputGroup>
                      
                      <Box
                        maxH="200px"
                        overflowY="auto"
                        borderWidth="1px"
                        borderRadius="md"
                        p={3}
                        bg="gray.50"
                      >
                        {isLoading ? (
                          <Flex justify="center" p={4}>
                            <Spinner size="sm" />
                          </Flex>
                        ) : products.length > 0 ? (
                          <VStack align="stretch" spacing={2}>
                            {products.map((product) => {
                              // adjust id/name fields based on actual API response
                              const pid = product._id || product.id; 
                              const pname = product.name || product.title;
                              return (
                                <Checkbox
                                  key={pid}
                                  isChecked={list.productIds.includes(pid)}
                                  onChange={() => toggleProductInList(index, pid)}
                                >
                                  {pname}
                                </Checkbox>
                              );
                            })}
                          </VStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            محصولی یافت نشد.
                          </Text>
                        )}
                      </Box>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        {list.productIds.length} محصول انتخاب شده
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              ))}

              <Button colorScheme="blue" type="submit" alignSelf="flex-end" mt={4} isLoading={mutation.isLoading}>
                ذخیره اطلاعات
              </Button>
            </VStack>
          </Box>
        </CardBody>
      </Card>
    </Flex>
  );
}
